/* eslint-disable @angular-eslint/no-host-metadata-property */
import { CloseScrollStrategy, Overlay, OverlayModule, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe, DatePipe, DOCUMENT, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import {
  BehaviorSubject,
  debounceTime,
  EMPTY,
  from,
  map,
  mergeMap,
  NEVER,
  Observable,
  of,
  tap,
  timer,
  Unsubscribable,
} from 'rxjs';
import { GetCoordinatesByAddressGQL } from 'src/app/pages/search-results/search-results.query.generated';
import { Category, SeasonEnum, SuggestedSearch, UserSearch } from 'src/types/graphql';
import { getSeasonDateRange, isCurrentSeason } from 'utils/date-utils';
import { centerOfRegions, municipalities, places, placesKeys, placesShort, regions } from 'utils/places-utility';
import { SearchResultsFilterablesService } from 'utils/search-results-filterable';
import { BasePlusComponent } from '../../base/base-plus.component';
import { EllipsisTooltipDirective } from '../../../../common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { FormScrollDirective } from '../../../../common/form-scroll/form-scroll.directive';
import { IconComponent } from '../../../../common/icon/icon.component';
import { LocationSearchService } from '../../../../common/location-search/location-search.service';
import { OpenSnackBarService } from '../../../../common/open-snack-bar/open-snack-bar.service';
import {
  AutocompletePlacesFragment,
  AutocompletePlacesGQL,
  AutocompletePlacesQuery,
} from '../../prefix-search/prefix-search.query.generated';
import { RouteTranslateService } from '../../../../common/route-translate/route-translate.service';
import { SlugService } from '../../../../common/slug/slug.service';
import { TypedTemplateDirective } from '../../../../common/typed-template/typed-template.directive';
import { UserService } from '../../../../common/user/user.service';
import { UserDeleteSearchesGQL, UserSearchCreateGQL } from './header-search.mutation.generated';
import {
  AutocompleteNoPlacesFragment,
  AutocompleteNoPlacesGQL,
  AutocompleteNoPlacesHashtagFragment,
  AutocompleteNoPlacesQuery,
  HeaderCategoriesGQL,
  SuggestedSearchesGQL,
  UserSearchesGQL,
} from './header-search.query.generated';
import { HeaderSearchService } from './header-search.service';
import { ParamsService } from '../../../../pages/search-results/params/params.service';

export const scrollFactory =
  (overlay: Overlay): (() => CloseScrollStrategy) =>
  () =>
    overlay.scrollStrategies.close({ threshold: 50 });

@Directive({
  selector: `[searchTriggerFor]`,
  host: {
    '(click)': '_handleClick($event)',
  },
  exportAs: 'searchTrigger',
  standalone: true,
})
export class HeaderSearchTriggerDirective {
  @Input() searchTriggerFor: HeaderSearchComponent;
  @Input() userSearchKind: UserSearchKind;

  _handleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.openSearch();
  }

  openSearch() {
    if (this.searchTriggerFor) {
      this.searchTriggerFor.onInputClick(this.userSearchKind || UserSearchKind.FindSearch);
    }
  }
}

@Component({
  selector: 'sharry-header-search',
  standalone: true,
  imports: [
    NgClass,
    AsyncPipe,
    NgStyle,
    NgTemplateOutlet,
    MatInputModule,
    MatButtonModule,
    IconComponent,
    I18NextModule,
    MatMenuModule,
    OverlayModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatExpansionModule,
    FormsModule,
    EllipsisTooltipDirective,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatListModule,
    TypedTemplateDirective,
    FormScrollDirective,
  ],
  templateUrl: './header-search.component.html',
  styleUrls: ['./header-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    DatePipe,
  ],
})
export class HeaderSearchComponent extends BasePlusComponent implements OnChanges {
  @ViewChild('templatePortalContent') templatePortalContent: TemplateRef<unknown>;
  @ViewChild('searchOrigin') origin: ElementRef<HTMLDivElement>;
  @ViewChild('findInput') findInput: ElementRef<HTMLInputElement>;
  @ViewChild('whereInput') whereInput: ElementRef<HTMLInputElement>;
  @ViewChild('whenInput') whenInput: ElementRef<HTMLInputElement>;
  @Input() menuExpanded: boolean = false;
  @Input() resetFilter: boolean = false;
  active = false;
  @Output() isComplete: EventEmitter<boolean> = new EventEmitter();
  @Output() clearFilters: EventEmitter<any> = new EventEmitter();
  @Output() clearWhere: EventEmitter<any> = new EventEmitter();
  @Output() clearWhen: EventEmitter<any> = new EventEmitter();
  @Output() clearFind: EventEmitter<any> = new EventEmitter();
  complete = false;
  activeType: UserSearchKind;
  activeSelection: 'sharry-tour' | 'activity' | 'item' = 'sharry-tour';
  scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close({ threshold: 50 });
  isFocusMode = false;
  previousSearchResult: string;
  seasons: { name: SeasonEnum; normalizedName: string; active: boolean }[] = [
    {
      name: SeasonEnum.Spring,
      normalizedName: SeasonEnum.Spring,
      active: false,
    },
    {
      name: SeasonEnum.Summer,
      normalizedName: SeasonEnum.Summer,
      active: false,
    },
    {
      name: SeasonEnum.Autumn,
      normalizedName: SeasonEnum.Autumn,
      active: false,
    },
    {
      name: SeasonEnum.Winter,
      normalizedName: SeasonEnum.Winter,
      active: false,
    },
  ];
  categoriesSubject: BehaviorSubject<(Category & { active: boolean })[]> = new BehaviorSubject([]);
  categories$: Observable<(Category & { active: boolean })[]> = this.categoriesSubject.asObservable();

  regions: {
    name: string;
    normalizedName: string;
    active: boolean;
    regions: string[];
    center: { latitude: number; longitude: number };
    zoom: number;
    distance: number;
    radius: number;
    hidden: boolean;
  }[] = [
    ...centerOfRegions
      .sort((a, b) => a.value.localeCompare(b.value))
      .map(r => ({
        name: r.value.replace(', Italia', ''),
        normalizedName: Object.keys(regions)
          .find(k => regions[k] === r.value)
          .concat('-blue'),
        active: false,
        regions: [r.value],
        center: { latitude: r.latitude, longitude: r.longitude },
        zoom: 9,
        distance: 150,
        radius: 120,
        hidden: false,
      })),
  ];

  sharrySearchTypes: {
    normalizedName: string;
    active: boolean;
    name: string;
    previewImage: {
      url: string;
    };
  }[] = [
    {
      name: 'tours',
      normalizedName: 'tour',
      active: false,
      previewImage: {
        url: 'https://media.sharry.land/app/images/families/ic-tour.webp',
      },
    },
    {
      name: 'experiences',
      normalizedName: 'experience',
      active: false,
      previewImage: {
        url: 'https://media.sharry.land/app/images/families/ic-experience.webp',
      },
    },
    {
      name: 'items',
      normalizedName: 'item',
      active: false,
      previewImage: {
        url: 'https://media.sharry.land/app/images/families/ic-item.webp',
      },
    },
    // {
    //   name: 'rentals',
    //   normalizedName: 'rental',
    //   active: false,
    //   previewImage: {
    //     url: 'https://media.sharry.land/app/images/families/ic-rental.webp',
    //   },
    // },
    // {
    //   name: 'accommodations',
    //   normalizedName: 'accommodation',
    //   active: false,
    //   previewImage: {
    //     url: 'https://media.sharry.land/app/images/families/ic-accommodation.webp',
    //   },
    // },
  ];
  searchTypes: {
    normalizedName: string;
    active: boolean;
    name: string;
    previewImage: {
      url: string;
    };
  }[] = [
    {
      name: 'wonders',
      normalizedName: 'wonder',
      active: false,
      previewImage: {
        url: 'https://media.sharry.land/app/images/families/ic-wonder.webp',
      },
    },
    {
      name: 'events',
      normalizedName: 'event',
      active: false,
      previewImage: {
        url: 'https://media.sharry.land/app/images/families/ic-event.webp',
      },
    },
    {
      name: 'users',
      normalizedName: 'users',
      active: false,
      previewImage: {
        url: 'https://media.sharry.land/app/images/families/ic-user.webp',
      },
    },
  ];

  searchBar = '';
  overlayRef: OverlayRef;
  types: UserSearchKind[] = [UserSearchKind.FindSearch, UserSearchKind.WhereSearch, UserSearchKind.WhenSearch];
  icons: { [K in UserSearchKind]: string } = {
    find_search: 'target',
    when_search: 'calendar-blank',
    where_search: 'map-pin',
  };

  searchGroup: FormGroup<{
    find_search: FormControl<string>;
    where_search: FormControl<string>;
    when_search: FormControl<string>;
  }> = new FormGroup({
    find_search: new FormControl(),
    where_search: new FormControl(),
    when_search: new FormControl(),
  });
  findAutocompleteVisible = false;
  whereAutocompleteVisible = false;
  findAutocompleteOptions$: Observable<AutocompleteNoPlacesFragment[]>;
  whereAutocompleteOptions$: Observable<AutocompletePlacesFragment[]>;
  userSearchKind = UserSearchKind;

  autocompleteToken: { results: any[]; searchType: UserSearchKind };
  startDate: Date;
  endDate: Date;
  minDate: Date = new Date();

  params: Params = {};
  mobileIpad = this.isMobileIpad();
  userSearches$: Observable<UserSearch[]>;
  suggestedSearches$: Observable<Partial<SuggestedSearch>[]>;
  findFilterActives = 0;
  private overlaySubs: Unsubscribable[] = [];

  constructor(
    public userService: UserService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private overlay: Overlay,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private autocompleteNoPlacesGQL: AutocompleteNoPlacesGQL,
    private autocompletePlacesGQL: AutocompletePlacesGQL,
    private userSearchesGQL: UserSearchesGQL,
    private userSearchCreateGQL: UserSearchCreateGQL,
    private headerCategoriesGQL: HeaderCategoriesGQL,
    private routeTranslateService: RouteTranslateService,
    private slugService: SlugService,
    private headerSearchService: HeaderSearchService,
    private locationSearchService: LocationSearchService,
    private getCoordinatesByAddressGQL: GetCoordinatesByAddressGQL,
    @Inject(DOCUMENT) private document: Document,
    private datePipe: DatePipe,
    private userDeleteSearchesGQL: UserDeleteSearchesGQL,
    private searchResultsFilterablesService: SearchResultsFilterablesService,
    private suggestedSearchesGQL: SuggestedSearchesGQL,
    private openSnackBarService: OpenSnackBarService,
    @Inject(I18NEXT_SERVICE) private transalateService: ITranslationService,
    private route: ActivatedRoute,
    private paramsService: ParamsService,
  ) {
    super();
    this.findAutocompleteOptions$ = this.searchGroup.get(UserSearchKind.FindSearch)?.valueChanges?.pipe(
      debounceTime(150),
      mergeMap(value => {
        if (
          typeof value === 'string' &&
          value
            ?.split(' ')
            .join('')
            .replace(/[\u2018\u2019]/g, "'").length
        ) {
          const text = value;
          if (typeof text !== 'string' || text?.length < 2) {
            if (!this.isMobile() && this.searchGroup.get(UserSearchKind.FindSearch)?.value) {
              this.findAutocompleteVisible = false;
              this.onOpenActive();
              this.cd.detectChanges();
            }
            return of({ data: { results: [] } } as ApolloQueryResult<AutocompleteNoPlacesQuery>);
          }
          return this.autocompleteNoPlacesGQL.fetch({
            text: text
              .replace(/[\u2018\u2019]/g, "'")
              .replace('#', '')
              .replace('«', '')
              .replace('»', '')
              .replace(/\\/g, ''),
            limit: 5,
          });
        }
        if (!this.isMobile() && !this.searchGroup.get(UserSearchKind.FindSearch)?.value) {
          this.onOpenActive();
        }
        return of({ data: { results: [] } } as ApolloQueryResult<AutocompleteNoPlacesQuery>);
      }),
      map(r => {
        this.cd.detectChanges();
        return r.data.results;
      }),
      tap(r => {
        if (!this.isMobile()) {
          if (r.length) {
            this.onCloseActive();
          }
          this.findAutocompleteVisible = !!r.length;
        }
        this.cd.detectChanges();
      }),
    );
    this.whereAutocompleteOptions$ = this.searchGroup.get(UserSearchKind.WhereSearch)?.valueChanges.pipe(
      debounceTime(150),
      mergeMap(value => {
        if (
          typeof value === 'string' &&
          value
            ?.split(' ')
            .join('')
            .replace(/[\u2018\u2019]/g, "'").length
        ) {
          const text = value;
          if (typeof text !== 'string' || text?.length < 2) {
            if (!this.isMobile() && this.searchGroup.get(UserSearchKind.WhereSearch)?.value) {
              this.whereAutocompleteVisible = false;
              this.onOpenActive();
              this.cd.detectChanges();
            }
            return of({ data: { results: [] } } as ApolloQueryResult<AutocompletePlacesQuery>);
          }
          return this.autocompletePlacesGQL.fetch({
            text: text
              .split(' ')
              .join('')
              .replace(/[\u2018\u2019]/g, "'")
              .replace('#', '')
              .replace('«', '')
              .replace('»', '')
              .replace(/\\/g, ''),
            limit: 5,
          });
        }
        if (!this.isMobile() && !this.searchGroup.get(UserSearchKind.WhereSearch)?.value) {
          this.onOpenActive();
        }
        return of({ data: { results: [] } } as ApolloQueryResult<AutocompletePlacesQuery>);
      }),
      map(r => r.data.results),
      tap(r => {
        if (!this.isMobile()) {
          if (r.length) {
            // this.onCloseActive();
          }
          this.whereAutocompleteVisible = !!r.length;
        }
        this.cd.detectChanges();
      }),
    );
    this.userSearches$ = this.userSearchesGQL
      .watch({}, { fetchPolicy: 'cache-first', nextFetchPolicy: 'cache-and-network' })
      .valueChanges.pipe(map(r => r.data.userSearches as UserSearch[]));
    this.suggestedSearches$ = this.suggestedSearchesGQL.fetch().pipe(map(r => r.data.suggestedSearches));
    const queryParams = this.headerSearchService.getQueryParams();
    if (queryParams) {
      this.handleQueryParams(queryParams);
    } else {
      this.subscriptions.push(
        this.route.queryParams.subscribe(params => {
          this.headerSearchService.setQueryParams(params);
          this.handleQueryParams(params);
        }),
      );
    }
    this.subscriptions.push(
      this.headerCategoriesGQL.fetch().subscribe(res => {
        this.categoriesSubject.next(
          res.data.allCategories
            .slice(0, 8)
            .map(c => ({ ...c, active: this.params?.searchCategories?.includes(c.id) })) as any,
        );
      }),
      this.route.queryParams.subscribe(params => {
        if (JSON.stringify(this.params) !== JSON.stringify(params)) {
          this.handleQueryParams(params);
        }
      }),
    );
    this.findAutocompleteVisible = false;
    this.whereAutocompleteVisible = false;
  }

  handleQueryParams(queryParams: Params) {
    this.params = queryParams;
    if (queryParams.prefix === 'title') {
      this.searchGroup.get(UserSearchKind.FindSearch).setValue(queryParams.value);
      if (queryParams.region) {
        this.regions = this.regions.map(s => ({ ...s, active: s.name === queryParams.region.replace(', Italia', '') }));
      }
      if (queryParams.province) {
        this.searchGroup.get(UserSearchKind.WhereSearch).setValue(places[queryParams.province]);
      }
      if (queryParams.city) {
        this.searchGroup.get(UserSearchKind.WhereSearch).setValue(queryParams.city + ', Italia');
      }
    } else {
      if (queryParams.value) {
        this.regions = this.regions.map(s => ({ ...s, active: s.name === queryParams.value.replace(', Italia', '') }));
      }
    }
    if (queryParams.beginDate && queryParams.endDate) {
      this.startDate = new Date(queryParams.beginDate);
      this.endDate = new Date(queryParams.endDate);
      const today = new Date(new Date().setHours(0, 0, 0, 0));
      if (
        this.seasons.find(s => {
          const dateToMatch = isCurrentSeason(s.name) ? today : getSeasonDateRange(s.name).start;
          return (
            this.startDate.getTime() === dateToMatch.getTime() &&
            this.endDate.getTime() === getSeasonDateRange(s.name).end.getTime()
          );
        })
      ) {
        this.seasons.find(s => {
          const dateToMatch = isCurrentSeason(s.name) ? today : getSeasonDateRange(s.name).start;
          return (
            this.startDate.getTime() === dateToMatch.getTime() &&
            this.endDate.getTime() === getSeasonDateRange(s.name).end.getTime()
          );
        }).active = true;
      }
      const start = this.datePipe.transform(queryParams.beginDate, 'dd/MM/yy');
      const end = this.datePipe.transform(queryParams.endDate, 'dd/MM/yy');
      this.searchGroup.get(UserSearchKind.WhenSearch).setValue(`${start} - ${end}`);
    }
    if (queryParams.types?.length) {
      this.searchTypes = this.searchTypes.map(z => ({
        ...z,
        active:
          z.normalizedName !== 'users' &&
          this.searchResultsFilterablesService?.filterables[z.normalizedName as 'wonder']
            .map(f => f.id)
            .every(c => queryParams.types.includes(c)),
      }));
      this.sharrySearchTypes = this.sharrySearchTypes.map(z => ({
        ...z,
        active: this.searchResultsFilterablesService?.filterables[z.normalizedName as 'tour']
          .map(f => f.id)
          .every(c => queryParams.types.includes(c)),
      }));
    }
    if (queryParams.searchQualifications?.length) {
      this.searchTypes.find(s => s.normalizedName === 'users').active =
        this.searchResultsFilterablesService?.filterables?.users
          .map(f => f.id)
          .every(c => queryParams.searchQualifications.includes(c));
    }
  }

  @HostListener('keyup.enter', ['$event']) onEnter() {
    if (this.searchGroup.get(this.userSearchKind.FindSearch)?.value) {
      this.onSearch();
    } else if (this.searchGroup.get(this.userSearchKind.WhereSearch)?.value) {
      this.onSearch();
    }
  }

  saveSearch(searchUrl: string, searchString: string) {
    if (!searchUrl || !searchString || !this.userService.user) {
      return of(true);
    }
    return this.userSearchCreateGQL
      .mutate(
        {
          input: {
            searchUrl,
            searchString,
          },
        },
        { refetchQueries: [this.userSearchesGQL.document] },
      )
      .pipe(map(response => true));
  }

  counterActiveBox(qualeFiltro: string) {
    switch (qualeFiltro) {
      case 'searchTypes':
        return this.searchTypes.length;
    }
    return 0;
  }

  onSearch(navigate = true) {
    this.headerSearchService.setBusy(true);
    this.cd.detectChanges();
    let effectiveValue: string;
    if (
      typeof this.searchGroup.get(UserSearchKind.FindSearch)?.value === 'string' &&
      !this.searchGroup.get(UserSearchKind.FindSearch)?.value.trim()?.length
    ) {
      this.searchGroup.get(UserSearchKind.FindSearch)?.setValue(null);
    }
    const findValue = this.searchGroup.get(UserSearchKind.FindSearch)?.value;
    if (findValue && typeof findValue === 'string') {
      effectiveValue = findValue;
    }
    this.subscriptions.push(
      of(true)
        .pipe(
          mergeMap(() => {
            const whereValue = this.searchGroup.get(UserSearchKind.WhereSearch)?.value;
            if (typeof whereValue === 'string') {
              whereValue.trim();
              if (!whereValue?.length) {
                this.searchGroup.get(UserSearchKind.WhereSearch)?.setValue(null);
              } else {
                return this.autocompletePlacesGQL
                  .fetch({
                    text: this.searchGroup
                      .get(UserSearchKind.WhereSearch)
                      .value.split(' ')
                      .join('')
                      .replace(/[\u2018\u2019]/g, "'")
                      .replace('#', '')
                      .replace('«', '')
                      .replace('»', '')
                      .replace(/\\/g, ''),
                    limit: 1,
                  })
                  .pipe(
                    mergeMap(r => {
                      if (r.data.results?.[0]?.title) {
                        this.searchGroup.get(UserSearchKind.WhereSearch)?.setValue(r.data.results[0]?.title);
                        this.onAutocomplete(r.data?.results[0], true);
                      } else {
                        this.searchGroup.get(UserSearchKind.WhereSearch)?.setValue(null);
                      }
                      return of(true);
                    }),
                  );
              }
            }
            return of(true);
          }),
          mergeMap(() => this.retrieveCoordinatesFromParams()),
          mergeMap(params => {
            const queryParams = { ...params };
            if (effectiveValue) {
              queryParams.value = effectiveValue;
              queryParams.prefix = 'title';
            } else {
              let value: string;
              const findValue = this.searchGroup.get(UserSearchKind.FindSearch)?.value;
              if (findValue) {
                value =
                  '#' + (typeof findValue === 'string' ? findValue : (findValue as any).hashtagName).replace('#', '');
              }
              const whereValue = this.searchGroup.get(UserSearchKind.WhereSearch)?.value;
              if (whereValue && !findValue) {
                queryParams.prefix = 'location';
                value = typeof whereValue === 'string' ? whereValue : (whereValue as any).title;
              }
              queryParams.value = value || '';
            }
            if (!queryParams.prefix) {
              queryParams.prefix = 'title';
            }
            if (!this.searchGroup.get(UserSearchKind.WhereSearch)?.value) {
              delete queryParams.latitude;
              delete queryParams.longitude;
              delete queryParams.region;
              delete queryParams.province;
              delete queryParams.distance;
              delete queryParams.municipality;
              if (queryParams.prefix === 'location' && !findValue) {
                queryParams.prefix = 'title';
                queryParams.value = '';
              }
            }
            queryParams.searchCategories = this.categoriesSubject.value.filter(c => c.active).map(c => c.id);
            queryParams.types = [
              ...this.searchTypes
                .filter(c => c.active && c.normalizedName !== 'users')
                .map(c =>
                  (this.searchResultsFilterablesService?.filterables?.[c.normalizedName as 'wonder'] || []).map(
                    f => f.id,
                  ),
                )
                .flat(),
              ...this.sharrySearchTypes
                .filter(c => c.active)
                .map(c =>
                  (this.searchResultsFilterablesService?.filterables?.[c.normalizedName as 'tour'] || []).map(
                    f => f.id,
                  ),
                )
                .flat(),
            ];

            queryParams.withUsers = this.searchTypes.some(c => c.normalizedName === 'users' && c.active);
            queryParams.searchQualifications = queryParams.withUsers
              ? this.searchResultsFilterablesService?.filterables.users.map(f => f.id)
              : undefined;
            if (!this.searchGroup.get(UserSearchKind.WhereSearch)?.value) {
              const activeRegion = this.regions.find(c => c.active);
              if (activeRegion) {
                if (!this.searchGroup.get(UserSearchKind.FindSearch)?.value) {
                  queryParams.prefix = 'location';
                  queryParams.value = activeRegion.regions[0];
                }
                queryParams.region = activeRegion.regions[0];
              }
            }
            queryParams.beginDate = this.startDate ? this.startDate.toISOString() : undefined;
            queryParams.endDate = this.endDate ? this.endDate.toISOString() : undefined;
            this.headerSearchService.setQueryParams(queryParams);
            if (navigate) {
              this.onCloseActive();
              const searchParams = { ...queryParams };
              const value = searchParams.value;
              delete searchParams.value;
              delete searchParams.latitude;
              delete searchParams.longitude;
              delete searchParams.prefix;
              delete searchParams.distance;
              delete searchParams.withUsers;
              if (searchParams.beginDate) {
                searchParams.beginDate = this.datePipe.transform(searchParams.beginDate, 'dd/MM/yy');
              }
              if (searchParams.endDate) {
                searchParams.endDate = this.datePipe.transform(searchParams.endDate, 'dd/MM/yy');
              }
              if (searchParams.searchCategories?.length) {
                searchParams.searchCategories = searchParams.searchCategories.map(
                  (id: string) => this.categoriesSubject.value.find(c => c.id === id).name,
                );
              }
              if (searchParams.types?.length) {
                const types = [
                  ...this.searchTypes
                    .filter(
                      s =>
                        s.name !== 'users' &&
                        this.searchResultsFilterablesService?.filterables[s.normalizedName as 'wonder']
                          .map(f => f.id)
                          .every(c => queryParams.types.includes(c)),
                    )
                    .map(d => d.name),
                  ...this.sharrySearchTypes
                    .filter(s =>
                      (this.searchResultsFilterablesService?.filterables?.[s.normalizedName as 'tour'] || [])
                        .map(f => f.id)
                        .every(c => queryParams.types.includes(c)),
                    )
                    .map(d => d.name),
                ];
                delete searchParams.types;
                if (types.length) {
                  searchParams.types = types.map(t => this.transalateService.t(t));
                }
              }
              if (searchParams.searchQualifications?.length) {
                const searchQualifications = this.searchResultsFilterablesService?.filterables.users
                  .map(f => f.id)
                  .every(c => queryParams.searchQualifications.includes(c));
                delete searchParams.searchQualifications;
                if (searchQualifications) {
                  searchParams.searchQualifications = this.transalateService.t('users');
                }
              }
              let searchString = Object.values(searchParams)
                .filter(f => {
                  if (f && typeof f !== 'object') return !!f;
                  return !!f?.length;
                })
                .reduce((acc, curr, i) => {
                  const separator = i === 0 ? '' : ' - ';
                  if (typeof curr !== 'object') {
                    acc += separator + curr;
                  } else {
                    if (curr.length) acc += separator + curr.join(', ');
                  }
                  return acc;
                }, '');
              if (value && value.trim().length > 0) {
                searchString = value.concat(searchString.length ? ' - ' + searchString : '');
              }
              if (queryParams?.types) {
                this.paramsService.setKindsFromUrlTypes(queryParams?.types);
              }
              return this.saveSearch(
                this.router
                  .createUrlTree([this.routeTranslateService.translateRoute('search-results')], {
                    queryParams,
                  })
                  .toString(),
                searchString,
              ).pipe(map(() => queryParams));
            } else {
              return EMPTY;
            }
          }),
          mergeMap(queryParams =>
            from(
              this.router.navigate([this.routeTranslateService.translateRoute('search-results')], {
                queryParams,
              }),
            ),
          ),
        )
        .subscribe(() => {
          this.onClose();
        }),
    );
  }

  onAutocomplete(autocomplete: AutocompleteNoPlacesFragment | AutocompletePlacesFragment, forceWhere = false) {
    if (
      autocomplete?.__typename === 'User' ||
      autocomplete?.__typename === 'Event' ||
      autocomplete?.__typename === 'Sharry' ||
      autocomplete?.__typename === 'Wonder'
    ) {
      this.router.navigate([this.slugService.slug(autocomplete)]);
      this.params = {};
      this.searchGroup.get(UserSearchKind.FindSearch).setValue(null);
      this.searchGroup.get(UserSearchKind.WhereSearch).setValue(null);
      this.onClose();
      return;
    }
    let searchString = '';
    if (autocomplete?.__typename === 'Hashtag') {
      searchString = '#' + autocomplete?.hashtagName;
      if (this.isMobile()) {
        if (this.activeType) {
          this.searchGroup.get(this.activeType).setValue(searchString);
        }
        this.isFocusMode = false;
        this.cd.detectChanges();
      }
    } else if (autocomplete?.__typename === 'Place') {
      searchString = autocomplete?.title;
    }
    if (this.activeType === UserSearchKind.FindSearch && !forceWhere) {
      this.params = {
        ...this.params,
        prefix: 'title',
        value: searchString,
        zoom: (autocomplete as AutocompleteNoPlacesHashtagFragment).zoom,
      };
    } else if (this.activeType === UserSearchKind.WhereSearch || forceWhere) {
      // eslint-disable-next-line no-case-declarations
      const overwrite = !this.searchGroup.get(UserSearchKind.FindSearch).value;
      const isRegion = (autocomplete as AutocompletePlacesFragment).isRegion;
      const isProvince = (autocomplete as AutocompletePlacesFragment).isProvince;
      const isMunicipality = (autocomplete as AutocompletePlacesFragment).isMunicipality;
      this.params = {
        ...this.params,
        value: overwrite ? searchString : this.params.value || this.searchGroup.get(UserSearchKind.FindSearch)?.value,
        prefix: overwrite ? 'location' : 'title',
        distance: !isRegion && !isProvince ? 50 : undefined,
        latitude: (autocomplete as AutocompletePlacesFragment).latitude,
        longitude: (autocomplete as AutocompletePlacesFragment).longitude,
        region: isRegion
          ? Object.keys(regions).find(key => regions[key] === searchString.replace(/[\u2018\u2019]/g, "'"))
          : undefined,
        province: isProvince
          ? placesKeys[
              Object.keys(placesShort).find(key => placesShort[key] === searchString.replace(', Italia', '')) as 'BT'
            ]
          : undefined,
        municipality: isMunicipality
          ? Object.keys(municipalities).find(
              key => municipalities[key] === searchString.replace(/[\u2018\u2019]/g, "'"),
            )
          : undefined,
        city: !isRegion && !isProvince && !isMunicipality ? searchString.replace(', Italia', '') : undefined,
      };
    }
    if (
      this.isMobile() &&
      (typeof this.searchGroup.get(this.activeType)?.value !== 'string' ||
        this.activeType === UserSearchKind.WhereSearch)
    ) {
      if (this.activeType) {
        this.searchGroup.get(this.activeType).setValue(searchString);
      }
      this.isFocusMode = false;
      this.cd.detectChanges();
    }
  }

  onFocus() {
    this.isFocusMode = true;
    this.cd.detectChanges();
  }

  onRemove(activeType: UserSearchKind) {
    if (activeType === UserSearchKind.FindSearch) {
      this.searchGroup.get(UserSearchKind.FindSearch).setValue('');
      this.params.value = '';
      if (this.searchGroup.get(UserSearchKind.WhereSearch)?.value?.length > 1) {
        this.params.prefix = 'location';
      }
    }
    if (activeType === UserSearchKind.WhereSearch) {
      this.searchGroup.get(UserSearchKind.WhereSearch).setValue(null);
      this.params.prefix = 'title';
      this.params.city = '';
      this.params.region = '';
      this.params.province = '';
    }
    this.isFocusMode = false;
  }

  onRemoveDate() {
    this.searchGroup.get(UserSearchKind.WhenSearch).setValue('');
    this.endDate = null;
    this.startDate = null;
    this.seasons = this.seasons.map(s => ({ ...s, active: false }));
    this.cd.detectChanges();
  }

  retrieveCoordinatesFromParams(): Observable<Params> {
    if (this.params?.prefix === 'location' && !this.params.latitude) {
      return this.getCoordinatesByAddressGQL
        .fetch({
          address: this.params?.value
            .replace(/[\u2018\u2019]/g, "'")
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\\/g, ''),
        })
        .pipe(
          map(r => r.data.coordinatesByAddress),
          map(({ latitude, longitude }) => ({ ...this.params, latitude, longitude })),
        );
    } else {
      return of(this.params);
    }
  }

  cleanSearchResult() {
    this.subscriptions.push(
      this.userDeleteSearchesGQL
        .mutate(null, {
          refetchQueries: [this.userSearchesGQL.document],
        })
        .subscribe(),
    );
  }

  onRecentSearch(result: string) {
    switch (this.activeType) {
      case UserSearchKind.FindSearch:
        this.searchGroup.get(UserSearchKind.FindSearch).setValue(result);
        this.onSearch();
        break;
      case UserSearchKind.WhereSearch:
        this.searchGroup.get(UserSearchKind.WhereSearch).setValue(result);
        this.onAutocomplete({} as AutocompletePlacesFragment);
        this.onSearch();
        break;
      case UserSearchKind.WhenSearch:
        break;
    }
  }

  onSeasonClick(box: { name: SeasonEnum; active: boolean }) {
    if (box.active) {
      this.onRemoveDate();
    } else {
      this.seasons.forEach(s => (s.active = s.name === box.name));
      if (isCurrentSeason(box.name)) {
        this.startDate = new Date(new Date().setHours(0, 0, 0, 0));
        this.minDate = new Date(new Date().setHours(0, 0, 0, 0));
      } else {
        this.startDate = getSeasonDateRange(box.name).start;
      }
      this.endDate = getSeasonDateRange(box.name).end;
      this.onDateChange();
    }
  }

  onRegionClick(box: { name: string; normalizedName: string; active: boolean; regions: string[] }) {
    this.searchGroup.get(UserSearchKind.WhereSearch).setValue(null);
    if (!box.active) {
      this.regions = this.regions.map(s => ({ ...s, active: s.name === box.name }));
    } else {
      this.regions = this.regions.map(s => ({ ...s, active: false }));
    }

    this.cd.detectChanges();
  }

  resetWhere() {
    this.searchGroup.get(UserSearchKind.WhereSearch).setValue(null);
    this.regions = this.regions.map(s => ({ ...s, active: false }));
  }

  onBoxClick(box: { name: any; active: boolean }, filter: UserSearchKind) {
    switch (filter) {
      case UserSearchKind.FindSearch:
        box.active = !box.active;
        break;
      case UserSearchKind.WhereSearch:
        this.onRegionClick(box as any);
        break;
      case UserSearchKind.WhenSearch:
        this.onSeasonClick(box);
        break;
    }
    this.cd.detectChanges();
  }

  ngOnChanges() {
    if (this.menuExpanded && this.active) {
      this.onClose();
    }
    if (this.resetFilter) {
      this.onClearFilters();
    }
  }

  onInputClick(type: UserSearchKind, changeActiveStatus = true) {
    if (this.document.getElementById('dialog__structure__search')) {
      this.document.getElementById('dialog__structure__search').scrollTo(0, 0);
    }
    if (changeActiveStatus) {
      this.active = true;
      if (this.document.querySelector('.header__search'))
        this.renderer.addClass(this.document.querySelector('.header__search'), 'header__search--active');
      this.subscriptions.push(
        timer(150).subscribe(() => {
          this.complete = true;
          this.isComplete.emit(true);
          const positionStrategy = this.isMobile()
            ? this.overlay.position().global()
            : this.overlay
                .position()
                .flexibleConnectedTo(this.origin)
                .withFlexibleDimensions(true)
                .withPositions([
                  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 12 },
                ]);
          const scrollStrategy = this.isMobile()
            ? this.overlay.scrollStrategies.block()
            : this.overlay.scrollStrategies.close({ threshold: 50 });
          this.overlayRef = this.overlay.create({
            backdropClass: this.isMobile() ? undefined : 'sharry-no-backdrop--header-search',
            hasBackdrop: !this.isMobile(),
            disposeOnNavigation: true,
            scrollStrategy,
            positionStrategy,
          });
          this.overlayRef.attach(new TemplatePortal(this.templatePortalContent, this.viewContainerRef));
          this.overlaySubs.push(
            this.overlayRef.backdropClick().subscribe(() => this.onClose()),
            this.overlayRef.detachments().subscribe(() => this.onClose()),
          );
          this.cd.detectChanges();
          let input: HTMLInputElement;
          switch (type) {
            case UserSearchKind.FindSearch:
              input = this.findInput.nativeElement;
              break;
            case UserSearchKind.WhereSearch:
              input = this.whereInput.nativeElement;
              break;
            case UserSearchKind.WhenSearch:
              input = this.whenInput.nativeElement;
              break;
          }
          if (!this.isMobile()) input?.focus({ preventScroll: true });
          this.overlayRef.updatePosition();
        }),
      );
    }
    this.findAutocompleteVisible = false;
    this.whereAutocompleteVisible = false;
    if (!this.isMobile()) {
      this.onOpenActive();
      this.onChooseFilter(type);
    }
  }

  autoCompleteWhere() {
    this.subscriptions.push(
      of(true)
        .pipe(
          mergeMap(() => {
            if (
              typeof this.searchGroup.get(UserSearchKind.WhereSearch)?.value === 'string' &&
              this.searchGroup.get(UserSearchKind.WhereSearch)?.value.trim().length
            ) {
              return this.autocompletePlacesGQL.fetch({
                text: this.searchGroup
                  .get(UserSearchKind.WhereSearch)
                  .value.replace(/[\u2018\u2019]/g, "'")
                  .replace('#', '')
                  .replace('«', '')
                  .replace('»', '')
                  .replace(/\\/g, ''),
                limit: 1,
              });
            }
            return NEVER;
          }),
        )
        .subscribe(r => {
          if (r.data.results?.[0]?.title) {
            this.searchGroup.get(UserSearchKind.WhereSearch).setValue(r.data.results[0] as any);
            this.onAutocomplete(r.data.results[0], true);
          } else {
            this.searchGroup.get(UserSearchKind.WhereSearch).setValue('');
          }
        }),
    );
  }

  setEndDateIfStart() {
    if (this.startDate && !this.endDate) {
      this.endDate = this.startDate;
    }
  }

  onDateChange() {
    if (!!this.startDate && !!this.endDate) {
      const start = this.datePipe.transform(this.startDate, 'dd/MM/yy');
      const end = this.datePipe.transform(this.endDate, 'dd/MM/yy');
      this.searchGroup.get(UserSearchKind.WhenSearch).setValue(`${start} - ${end}`);
    } else {
      this.searchGroup.get(UserSearchKind.WhenSearch).setValue('');
    }
    this.cd.detectChanges();
  }

  onLocationSearch() {
    this.onClose(this.isFocusMode);
    this.locationSearchService.onSearch();
  }

  onCloseActive() {
    this.overlayRef?.addPanelClass('hidden');
  }

  onOpenActive() {
    this.overlayRef?.removePanelClass('hidden');
  }

  onClose(isFocusMode?: boolean, url?: string) {
    if (url) {
      if (url !== this.previousSearchResult) {
        this.headerSearchService.setBusy(true);
      }
      this.previousSearchResult = url;
    }
    if (isFocusMode) {
      this.isFocusMode = false;
    } else {
      this.active = false;
      this.complete = false;
      this.isComplete.emit(false);
      this.overlayRef.dispose();
      this.overlaySubs.forEach(s => s.unsubscribe());
      this.activeType = null;
      if (this.document.querySelector('.header__search')) {
        this.renderer.removeClass(this.document.querySelector('.header__search'), 'header__search--active');
      }
    }
    this.cd.detectChanges();
  }

  onChooseFilter(filter: UserSearchKind) {
    this.activeType = filter;
    this.document.getElementsByClassName('dialog-mobile__body')[0]?.scroll(0, 0);
    this.cd.detectChanges();
  }

  displayWith(option: any): string {
    if (typeof option === 'string') return option;
    return option?.title || option?.fullname || (option?.hashtagName ? '#' + option?.hashtagName : null);
  }

  onReset() {
    this.onClearFilters();
    this.document.querySelector('mat-expansion-panel-header').parentElement.click();
    import('src/app/_shared/components/generic-snack-bar/generic-snack-bar.component').then(
      ({ GenericSnackBarComponent }) =>
        this.openSnackBarService.openSnackBar(GenericSnackBarComponent, {
          data: { text: 'cleared-filters', close: true, customIcon: 'eraser', duration: 4000 },
        }),
    );
    this.cd.detectChanges();
  }

  private onClearFilters = () => {
    this.searchGroup.get(UserSearchKind.FindSearch).setValue(null);
    this.searchGroup.get(UserSearchKind.WhereSearch).setValue(null);
    this.searchGroup.get(UserSearchKind.WhenSearch).setValue(null);
    this.params = null;
    this.startDate = null;
    this.endDate = null;
    this.seasons = this.seasons.map(c => ({ ...c, active: false }));
    this.regions = this.regions.map(c => ({ ...c, active: false }));
    this.sharrySearchTypes = this.sharrySearchTypes.map(c => ({ ...c, active: false }));
    this.searchTypes = this.searchTypes.map(c => ({ ...c, active: false }));
    this.categoriesSubject.next(this.categoriesSubject.value.map(c => ({ ...c, active: false })));
    this.clearFilters.emit();
  };
}

export enum UserSearchKind {
  FindSearch = 'find_search',
  WhenSearch = 'when_search',
  WhereSearch = 'where_search',
}
